import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  ComputerDesktopIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ArrowDownTrayIcon,
  PencilIcon,
  XMarkIcon,
  CheckIcon,
  MapPinIcon,
} from "@heroicons/react/24/outline";
import { apiService } from "../../services/api";
import { Machine } from "../../types";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import AgentDownloadModal from "../../components/machines/AgentDownloadModal";
import MachineLimitCard from "../../components/machines/MachineLimitCard";
import EditBranchModal from "../../components/machines/EditBranchModal";
import AdvancedSearchModal from "../../components/machines/AdvancedSearchModal";

export default function MachinesPage() {
  const navigate = useNavigate();
  const [machines, setMachines] = useState<Machine[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [branchFilter, setBranchFilter] = useState("");
  const [advancedFilters, setAdvancedFilters] = useState<{
    software?: string;
    webHistory?: string;
    observation?: string;
    userName?: string;
    agentId?: string;
  }>({});
  const [isAdvancedSearchOpen, setIsAdvancedSearchOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalMachines, setTotalMachines] = useState(0);
  const [isAgentModalOpen, setIsAgentModalOpen] = useState(false);
  const [editingObservation, setEditingObservation] = useState<string | null>(
    null
  );
  const [observationValue, setObservationValue] = useState("");
  const [isSavingObservation, setIsSavingObservation] = useState(false);

  // Branch edit state
  const [isBranchModalOpen, setIsBranchModalOpen] = useState(false);
  const [selectedMachineForBranch, setSelectedMachineForBranch] = useState<{
    id: string;
    name: string;
    branchId: string | null;
  } | null>(null);

  const itemsPerPage = 10;

  const loadMachines = useCallback(async () => {
    try {
      setIsLoading(true);
      const params: any = {
        search: searchTerm || undefined,
        status: statusFilter || undefined,
        branch: branchFilter || undefined,
        page: currentPage,
        limit: itemsPerPage,
      };

      // Adicionar filtros avançados
      if (advancedFilters.software) {
        params.software = advancedFilters.software;
      }
      if (advancedFilters.webHistory) {
        params.web_history = advancedFilters.webHistory;
      }
      if (advancedFilters.observation) {
        params.observation = advancedFilters.observation;
      }
      if (advancedFilters.userName) {
        params.user_name = advancedFilters.userName;
      }
      if (advancedFilters.agentId) {
        params.agent_id = advancedFilters.agentId;
      }

      const response = await apiService.getMachines(params);
      if (response && response.data) {
        setMachines(response.data);
        setTotalMachines(response.total || 0);
        setTotalPages(Math.ceil((response.total || 0) / itemsPerPage));
      } else {
        setError("Erro: resposta inválida do servidor");
        setMachines([]);
        setTotalMachines(0);
        setTotalPages(1);
      }
    } catch (err: any) {
      setError("Erro ao carregar máquinas");
      console.error("Machines error:", err);
    } finally {
      setIsLoading(false);
    }
  }, [searchTerm, statusFilter, branchFilter, advancedFilters, currentPage, itemsPerPage]);

  useEffect(() => {
    loadMachines();
  }, [loadMachines]);

  // loadMachines is defined above using useCallback

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "online":
        return <CheckCircleIcon className="w-4 h-4 text-green-500" />;
      case "offline":
        return <XCircleIcon className="w-4 h-4 text-red-500" />;
      case "warning":
        return <ExclamationTriangleIcon className="w-4 h-4 text-yellow-500" />;
      default:
        return <ComputerDesktopIcon className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const baseClasses =
      "inline-flex items-center px-2 py-0.5 rounded text-xs font-medium";
    switch (status) {
      case "online":
        return `${baseClasses} bg-green-100 text-green-800`;
      case "offline":
        return `${baseClasses} bg-red-100 text-red-800`;
      case "warning":
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  const formatLastSeen = (lastSeen: string) => {
    if (!lastSeen) return "N/A";
    
    // Show full date and time like in machine details page
    const date = new Date(lastSeen.endsWith("Z") ? lastSeen : lastSeen + "Z");
    return date.toLocaleString("pt-BR", {
      timeZone: "America/Sao_Paulo",
    });
  };

  const uniqueBranches = Array.from(
    new Set(machines.map((m) => m.branch).filter(Boolean))
  );

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    // loadMachines will be triggered by the useEffect when currentPage or filters change
  };

  const clearFilters = () => {
    setSearchTerm("");
    setStatusFilter("");
    setBranchFilter("");
    setAdvancedFilters({});
    setCurrentPage(1);
  };

  const handleAdvancedSearch = (filters: {
    software?: string;
    webHistory?: string;
    observation?: string;
  }) => {
    setAdvancedFilters(filters);
    setCurrentPage(1);
  };

  const handleEditObservation = (machine: Machine, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setEditingObservation(machine.id);
    setObservationValue(machine.observation || "");
  };

  const handleCancelEdit = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setEditingObservation(null);
    setObservationValue("");
  };

  const handleSaveObservation = async (
    machineId: string,
    e: React.MouseEvent
  ) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      setIsSavingObservation(true);
      await apiService.updateMachine(machineId, {
        observation: observationValue,
      });

      // Update local state
      setMachines(
        machines.map((m) =>
          m.id === machineId ? { ...m, observation: observationValue } : m
        )
      );

      setEditingObservation(null);
      setObservationValue("");
    } catch (err: any) {
      console.error("Error updating observation:", err);
      alert("Erro ao atualizar observação");
    } finally {
      setIsSavingObservation(false);
    }
  };

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="text-red-700">{error}</div>
        <button
          onClick={loadMachines}
          className="mt-2 text-red-600 hover:text-red-800 underline"
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
          Máquinas
        </h1>
        <div className="flex items-center space-x-3">
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {totalMachines} máquina{totalMachines !== 1 ? "s" : ""} encontrada
            {totalMachines !== 1 ? "s" : ""}
          </div>
          <button
            onClick={() => setIsAgentModalOpen(true)}
            className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
          >
            <ArrowDownTrayIcon className="h-4 w-4 mr-1.5" />
            Download Agente
          </button>
        </div>
      </div>

      <AgentDownloadModal
        isOpen={isAgentModalOpen}
        onClose={() => setIsAgentModalOpen(false)}
      />

      {/* Machine Limit Card */}
      <MachineLimitCard />

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
        <form onSubmit={handleSearch} className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="md:col-span-2">
              <label
                htmlFor="search"
                className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-0.5"
              >
                Buscar
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  id="search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-8 pr-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded leading-5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Nome da máquina ou filial..."
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="status"
                className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-0.5"
              >
                Status
              </label>
              <select
                id="status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="block w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">Todos</option>
                <option value="online">Online</option>
                <option value="offline">Offline</option>
                <option value="warning">Aviso</option>
              </select>
            </div>

            <div>
              <label
                htmlFor="branch"
                className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-0.5"
              >
                Filial
              </label>
              <select
                id="branch"
                value={branchFilter}
                onChange={(e) => setBranchFilter(e.target.value)}
                className="block w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">Todas</option>
                {uniqueBranches.map((branch) => (
                  <option key={branch} value={branch}>
                    {branch}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <button
                type="button"
                onClick={clearFilters}
                className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
              >
                Limpar filtros
              </button>
              {(advancedFilters.software || advancedFilters.webHistory || advancedFilters.observation || advancedFilters.userName || advancedFilters.agentId) && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300">
                  Filtros avançados ativos
                </span>
              )}
            </div>
            <div className="flex space-x-1.5">
              <button
                type="button"
                onClick={() => setIsAdvancedSearchOpen(true)}
                className={`inline-flex items-center px-3 py-1.5 border text-xs font-medium rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                  advancedFilters.software || advancedFilters.webHistory || advancedFilters.observation || advancedFilters.userName || advancedFilters.agentId
                    ? "border-blue-300 dark:border-blue-600 text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50"
                    : "border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                }`}
              >
                <MagnifyingGlassIcon className="w-3.5 h-3.5 mr-1.5" />
                Pesquisa Avançada
              </button>
              <button
                type="submit"
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <FunnelIcon className="w-3.5 h-3.5 mr-1.5" />
                Filtrar
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Machines Table */}
      <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-md">
        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <LoadingSpinner size="lg" />
          </div>
        ) : machines.length === 0 ? (
          <div className="text-center py-8">
            <ComputerDesktopIcon className="mx-auto h-10 w-10 text-gray-400" />
            <h3 className="mt-1.5 text-xs font-medium text-gray-900 dark:text-white">
              Nenhuma máquina encontrada
            </h3>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              Tente ajustar os filtros ou aguarde o registro de novas máquinas.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-200 dark:divide-gray-700">
            {machines.map((machine) => (
              <li
                key={machine.id}
                className="hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <div className="px-3 py-2.5 sm:px-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start flex-1">
                      <div className="flex-shrink-0">
                        {getStatusIcon(machine.status)}
                      </div>
                      <div className="ml-3 flex-1">
                        <div className="flex items-center">
                          <Link
                            to={`/machines/${machine.id}`}
                            className="text-xs font-medium text-blue-600 dark:text-blue-400 truncate hover:underline"
                            onClick={(e) => {
                              if (editingObservation === machine.id) {
                                e.preventDefault();
                              }
                            }}
                          >
                            {machine.name || machine.agent_id}
                          </Link>
                          <span
                            className={`ml-2 ${getStatusBadge(machine.status)}`}
                          >
                            {machine.status}
                          </span>
                        </div>

                        {/* Observation Field - One of the first */}
                        <div className="mt-1.5">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              {editingObservation === machine.id ? (
                                <div className="flex items-start space-x-1.5">
                                  <textarea
                                    value={observationValue}
                                    onChange={(e) =>
                                      setObservationValue(e.target.value)
                                    }
                                    placeholder="Digite uma observação..."
                                    rows={2}
                                    className="flex-1 px-2.5 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                  <div className="flex flex-col space-y-0.5">
                                    <button
                                      onClick={(e) =>
                                        handleSaveObservation(machine.id, e)
                                      }
                                      disabled={isSavingObservation}
                                      className="p-0.5 text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 disabled:opacity-50"
                                      title="Salvar"
                                    >
                                      <CheckIcon className="h-4 w-4" />
                                    </button>
                                    <button
                                      onClick={handleCancelEdit}
                                      className="p-0.5 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                                      title="Cancelar"
                                    >
                                      <XMarkIcon className="h-4 w-4" />
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div className="flex items-start justify-between group">
                                  <p className="text-xs text-gray-600 dark:text-gray-300 flex-1">
                                    <span className="font-medium">
                                      Observação:{" "}
                                    </span>
                                    {machine.observation || (
                                      <span className="text-gray-400 dark:text-gray-500 italic">
                                        Nenhuma observação
                                      </span>
                                    )}
                                  </p>
                                  <button
                                    onClick={(e) =>
                                      handleEditObservation(machine, e)
                                    }
                                    className="ml-1.5 p-0.5 text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 opacity-0 group-hover:opacity-100 transition-opacity"
                                    title="Editar observação"
                                  >
                                    <PencilIcon className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="mt-1.5 flex items-center text-xs text-gray-500 dark:text-gray-400">
                          <p>Usuário: {machine.user_name || "N/A"}</p>
                          {machine.branch && (
                            <>
                              <span className="mx-2">•</span>
                              <p>Filial: {machine.branch}</p>
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setSelectedMachineForBranch({
                                    id: machine.id,
                                    name: machine.name || machine.agent_id,
                                    branchId: machine.branch_id || null,
                                  });
                                  setIsBranchModalOpen(true);
                                }}
                                className="ml-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                                title="Alterar filial"
                              >
                                <PencilIcon className="h-3 w-3" />
                              </button>
                            </>
                          )}
                          {!machine.branch && (
                            <>
                              <span className="mx-2">•</span>
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setSelectedMachineForBranch({
                                    id: machine.id,
                                    name: machine.name || machine.agent_id,
                                    branchId: machine.branch_id || null,
                                  });
                                  setIsBranchModalOpen(true);
                                }}
                                className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 hover:underline flex items-center"
                                title="Definir filial"
                              >
                                Definir Filial
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 ml-3">
                      <p>
                        Última atividade: {formatLastSeen(machine.last_seen)}
                      </p>
                      {machine.location &&
                        machine.location.latitude &&
                        machine.location.longitude && (
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              navigate(`/?machineId=${machine.id}`);
                            }}
                            className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                            title="Ver localização no mapa"
                          >
                            <MapPinIcon className="w-3.5 h-3.5 mr-0.5" />
                            Ver localização
                          </button>
                        )}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="bg-white dark:bg-gray-800 px-3 py-2 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-4 rounded-lg shadow">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-xs font-medium rounded text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Anterior
            </button>
            <button
              onClick={() =>
                setCurrentPage(Math.min(totalPages, currentPage + 1))
              }
              disabled={currentPage === totalPages}
              className="ml-2 relative inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-xs font-medium rounded text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Próximo
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-xs text-gray-700 dark:text-gray-300">
                Mostrando{" "}
                <span className="font-medium">
                  {(currentPage - 1) * itemsPerPage + 1}
                </span>{" "}
                até{" "}
                <span className="font-medium">
                  {Math.min(currentPage * itemsPerPage, totalMachines)}
                </span>{" "}
                de <span className="font-medium">{totalMachines}</span>{" "}
                resultados
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded shadow-sm -space-x-px">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-1.5 py-1.5 rounded-l border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-xs font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Anterior
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const page = i + 1;
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`relative inline-flex items-center px-3 py-1.5 border text-xs font-medium ${
                        currentPage === page
                          ? "z-10 bg-blue-50 border-blue-500 text-blue-600 dark:bg-blue-900/50 dark:text-blue-400"
                          : "bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600"
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
                <button
                  onClick={() =>
                    setCurrentPage(Math.min(totalPages, currentPage + 1))
                  }
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-1.5 py-1.5 rounded-r border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-xs font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Próximo
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
      {/* Edit Branch Modal */}
      {selectedMachineForBranch && (
        <EditBranchModal
          isOpen={isBranchModalOpen}
          onClose={() => setIsBranchModalOpen(false)}
          onSuccess={() => {
            loadMachines();
            setIsBranchModalOpen(false);
          }}
          machineId={selectedMachineForBranch.id}
          currentBranchId={selectedMachineForBranch.branchId}
          machineName={selectedMachineForBranch.name}
        />
      )}

      {/* Advanced Search Modal */}
      <AdvancedSearchModal
        isOpen={isAdvancedSearchOpen}
        onClose={() => setIsAdvancedSearchOpen(false)}
        onSearch={handleAdvancedSearch}
        currentFilters={advancedFilters}
      />
    </div>
  );
}
